import React, { useContext, useState } from 'react';
import { instanceOf, string, bool } from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Rating } from '@thd-olt-component-react/rating';
import { Voting } from '../Voting/Voting.component';
import ReviewHelper from './helpers/ReviewHelper';
import './Review.styles.scss';
import { ReviewMediaCarousel } from './components/media-carousel';
import { usePresentation } from '../../context/PresentationProvider';

const badgeToClassConverter = (type) => {

  const classes = {
    DIY: 'ratings-reviews__badge-icons--diy',
    PRO: 'ratings-reviews__badge-icons--toolbox',
    TOP1CONTRIBUTOR: 'ratings-reviews__badge-icons--1',
    TOP10CONTRIBUTOR: 'ratings-reviews__badge-icons--10',
    TOP25CONTRIBUTOR: 'ratings-reviews__badge-icons--25',
    TOP50CONTRIBUTOR: 'ratings-reviews__badge-icons--50',
    TOP100CONTRIBUTOR: 'ratings-reviews__badge-icons--100',
    TOP250CONTRIBUTOR: 'ratings-reviews__badge-icons--250',
    TOP500CONTRIBUTOR: 'ratings-reviews__badge-icons--500',
    TOP1000CONTRIBUTOR: 'ratings-reviews__badge-icons--1k',
    EXPERT: 'ratings-reviews__badge-icons--star-outline',
    COMMUNITYASSOCIATE: 'ratings-reviews__badge-icons--',
    HOMEDEPOTASSOCIATE: 'ratings-reviews__badge-icons--associate',
    INCENTIVIZEDREVIEW: 'ratings-reviews__badge-icons--community',
    EARLYREVIEWERINCENTIVE: 'ratings-reviews__badge-icons--early_reviewer_program',
  };
  return classes[type];
};

const badgeToTextConverter = (type) => {

  const classes = {
    DIY: 'DIY',
    PRO: 'Pro',
    TOP1CONTRIBUTOR: '#1 Contributor',
    TOP10CONTRIBUTOR: 'Top 10 Contributor',
    TOP25CONTRIBUTOR: 'Top 25 Contributor',
    TOP50CONTRIBUTOR: 'Top 50 Contributor',
    TOP100CONTRIBUTOR: 'Top 100 Contributor',
    TOP250CONTRIBUTOR: 'Top 250 Contributor',
    TOP500CONTRIBUTOR: 'Top 500 Contributor',
    TOP1000CONTRIBUTOR: 'Top 1000 Contributor',
    EXPERT: 'Expert',
    COMMUNITYASSOCIATE: 'Community Associate',
    HOMEDEPOTASSOCIATE: 'Home Depot Associate',
    INCENTIVIZEDREVIEW: 'Seed ™ Member',
    EARLYREVIEWERINCENTIVE: 'The Home Depot Reviewer Program',
  };
  return classes[type];
};

/** THD Review component. */
export const Review = ({
  apiKey, review, searchTerm, shortenReviews
}) => {
  // TODO: Fixme
  const media = review.videos ? review.photos.concat(review.videos) : review.photos;
  const [showMore, setShowMore] = useState(false);
  const { id, reviewText, selected } = review;
  const { isServer, hosts } = useContext(ExperienceContext);
  const { useCondensedLayout } = usePresentation();
  const searchText = review.searchText || searchTerm;
  const serverHost = (hosts || {}).api;
  const hostName = isServer ?  serverHost : '';
  const reviewTextClasses = classNames('review-content-body', {
    'review-content-body--condensed': useCondensedLayout,
    'review-content-body--truncate': !showMore && ReviewHelper.shouldTruncate(reviewText),
    'review-content-body--expanded': showMore && ReviewHelper.shouldTruncate(reviewText)
  });

  const reviewItemClasses = classNames('review_item', {
    'review_item--condensed': useCondensedLayout,
  });

  const reviewUserClasses = classNames('review-user__wrapper', {
    'review-user--condensed': useCondensedLayout,
  });

  const reviewStatusIconsClasses = classNames('review-status-icons__list', {
    'review-status-icons__list--condensed': useCondensedLayout,
  });

  const reviewRatingsEarlyReviewClasses = classNames({
    'ratings-reviews__early-review': !useCondensedLayout,
  });

  const columnSize = (nominalColSize, condensedColSize) => (
    useCondensedLayout ? condensedColSize : nominalColSize
  );

  return (
    <div key={`review_${review.id}`} className={reviewItemClasses}>
      <Row>
        <Col xs={columnSize(2, 12)} className={reviewUserClasses}>
          <div className="review-user">
            <div className="review-user__name"> <a href={`https://homedepot.ugc.bazaarvoice.com/profiles/1999aa/${review.authorId}/profile.htm`} target="_blank" rel="noopener noreferrer"> {review.userNickname}</a> </div>
            <span className="review-user__age-gender">{review.userLocation}</span>
            <ul className="review-user__badge-list">

              <a href="http://homedepot.ugc.bazaarvoice.com/1999stores/badgedefinition.htm" target="_blank" rel="noopener noreferrer">
                {review.badgesOrder.filter((key) => (key.toUpperCase() !== 'VERIFIEDPURCHASER')).map((key, index) => <div key={`badge_${review.id}_${index}`}><li key={`review_${review.id}_badge_${index}`} className={badgeToClassConverter(key.toUpperCase())} /><span className="ratings-reviews__badge-text--full">{badgeToTextConverter(key.toUpperCase())}</span></div>)}
              </a>
            </ul>
          </div>
        </Col>
        <Col xs={columnSize(10, 12)}>
          <Row>
            <Col xs={columnSize(9, 12)}><span className="review-content__title">{ReviewHelper.getHighlightedText(id, review.title, searchTerm)}</span></Col>
          </Row>
          <Row>
            <Col xs={columnSize(6, 12)} className="review-content__col2">
              <span className="review-content__date">{ReviewHelper.formatSubmissionDate(review.submissionTime)}</span>
              <Rating value={review.rating} />
            </Col>
            <Col xs={columnSize(6, 12)} className="review-status-col">
              {
                review.verifiedRecommendedValues.length > 0
                  ? (
                    <div className="review-status-icons">
                      <ul className={reviewStatusIconsClasses}>
                        <li key={`review_${review.id}_recommended`} className="review-status-icons__item">
                          <a href="http://homedepot.ugc.bazaarvoice.com/1999stores/badgedefinition.htm" target="_blank" rel="noopener noreferrer">
                            <span className="ratings-reviews__badge-icons--recommended" />
                            <span className="ratings-reviews__badge-text--full">{review.verifiedRecommendedValues}</span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  )
                  : ''
              }
            </Col>
          </Row>
          {(review.secondaryRatingsExist || review.seedAccessProgramView || review.isSyndicated)
          && (
            <Row>
              <Col xs={columnSize(6, 12)} className="review-content__padding">
                {review.secondaryRatingsExist
              && (
                <div className="review-rating-secondary-ratings">
                  {
                    review.secondaryRatingsOrder.map((key, index) => {
                      if (!review.secondaryRatings[key]) {
                        return null;
                      }
                      return (
                        <div key={`secondary_rating_${index}`} className="review-secondary-ratings">
                          <div className="review-label">{review.secondaryRatings[key].Label} : </div>
                          <div className="review-dashed"><Rating value={review.secondaryRatings[key].Value} type="dash" orange /></div>
                          <div><span>{review.secondaryRatings[key].Value}</span></div>
                        </div>
                      );
                    })
                  }
                </div>
              )}
              </Col>
              <Col xs={columnSize(6, 12)}>
                <div className="review-rating-other-flags">
                  {review.seedAccessProgramView
                && (
                  <div>
                    <div className="review-rating-other-flags__seed-text">Seeds Program Review</div>
                    <a href="https://www.homedepot.com/c/home_depot_seeds_program" target="_blank" rel="noopener noreferrer">
                      <div className="review-rating-other-flags__what-text">(What&apos;s this)</div>
                    </a>
                  </div>
                )}
                  {
                    review.isSyndicated
                  && (
                    <div>
                      <span className="review-user__age-gender">Customer review from {review.syndicationSource.Name} <img alt="logo" src={review.syndicationSource.LogoImageUrl} loading="lazy" /></span>
                    </div>
                  )
                  }
                </div>
              </Col>
            </Row>
          )}

          <Row>

            <Col xs={columnSize(9, 12)} className="review-content">
              <div className="">
              {shortenReviews
                && ReviewHelper.shouldTruncate(reviewText) ?
                  // eslint-disable-next-line
                  <div
                    style={{ maxHeight: showMore ? ReviewHelper.getCalculatedReviewHeight(reviewText) + 'px' : '67px' }}
                    className={reviewTextClasses}
                    id={`review_${id}`}
                    onClick={() => setShowMore(!showMore)}
                  >
                    {ReviewHelper.getHighlightedText(id, reviewText, searchText)}
                  </div>
                  :
                  <div className="review-content-body">
                    {ReviewHelper.getHighlightedText(id, reviewText, searchText)}
                  </div>
                }
                <div className="review-metadata-keywords">
                  {review.pros ? <span className="review-metadata-keywords__label">Pros: <span className="review-metadata-keywords__list">{review.pros} </span></span> : ''}
                </div>
                <div className="review-metadata-keywords">
                  {review.cons ? <span className="review-metadata-keywords__label">Cons: <span className="review-metadata-keywords__list">{review.cons} </span></span> : ''}
                </div>
                {useCondensedLayout
                && (
                  <Col className="review-content__no-padding">
                    <span>By: </span>
                    <span>{review.userNickname}</span>
                  </Col>
                )}
                <ReviewMediaCarousel
                  media={media}
                  review={review}
                />
                {!useCondensedLayout
                && (
                  <div>
                    <Voting
                      reviewId={review.id}
                      totalPositiveFeedbackCount={review.totalPositiveFeedbackCount}
                      totalNegativeFeedbackCount={review.TotalNegativeFeedbackCount}
                      hostName={hostName}
                      apiKey={apiKey}
                    />
                  </div>
                )}
                {review.clientResponseText
                && (
                  <div className="review-client-response">
                    <div className="review-client-response__header">
                      <span className="review-client-response__title">Response from {review.clientResponseName}</span>
                      <span className="review-client-response__time">{ReviewHelper.formatSubmissionDate(review.clientResponseDate)}</span>
                    </div>
                    <div
                      className="review-client-response__content"
                      // eslint-disable-next-line
                      dangerouslySetInnerHTML={{ __html: (review.clientResponseText) }}
                    />
                  </div>
                )}
              </div>
            </Col>
            <Col xs={columnSize(3, 12)} className="review-content">
              {
                review.badgesOrder.length > 0 && review.badgesOrder.filter((key) => (key.toUpperCase() === 'EARLYREVIEWERINCENTIVE')).length !== 0
                && (
                  <div className={reviewRatingsEarlyReviewClasses}>
                    <div className="ratings-reviews__early-review--text">The Home Depot Reviewer Program</div>
                    <div className="ratings-reviews__early-review">
                      <a href="/c/early_reviewer_program" target="_blank" rel="noopener noreferrer" className="u__default-link">
                        (What&apos;s this?)
                      </a>
                    </div>
                  </div>
                )
              }
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="clear" />
    </div>
  );
};

Review.displayName = 'Review';

Review.propTypes = {
  apiKey: string.isRequired,
  review: instanceOf(Object).isRequired,
  searchTerm: string,
  shortenReviews: bool
};

Review.defaultProps = {
  searchTerm: '',
  shortenReviews: false
};
